import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Copy, ExternalLink, Info } from "lucide-react";
import { Loading } from "@/components/Loading";
import SubmittedFormsList from "@/features/onboarding/components/SubmittedFormsList";
import ExportLinksMenu from "@/features/onboarding/components/ExportLinksMenu";
import RegenerateLinkButton from "@/features/onboarding/components/RegenerateLinkButton";
import ShareLinkCard from "@/features/onboarding/components/ShareLinkCard";
import PricedLinksCard from "@/features/onboarding/components/PricedLinksCard";
import NextStepsCard from "@/features/onboarding/components/NextStepsCard";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/auth";
import { ApiError } from "@/lib/apiClient";
import { url } from "@/lib/basePath";
import { applicationCampaigns, type CampaignListItem } from "@/features/applications/api/applications.api";
import { onboarding, type OnboardingAgent } from "@/features/onboarding/api/onboarding.api";
import { FEATURES } from "@/config/featureFlags";
import { Notice } from "@/components/Notice";
import { AgentNumberSelect, AGENT_PAGE_SIZE, findAgentByNumber } from "@/features/onboarding/components/AgentSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NEXT_STEPS: ReactNode[] = [
  "Send your link to a merchant, or create a pre-priced link above.",
  "The merchant completes and submits the onboarding form.",
  "Configure pricing on the application and submit it. Pre-priced links skip this step.",
  "The onboarding team reviews it and sends it to the merchant to e-sign.",
];

export default function OnboardingFormPage() {
  const { user } = useAuth();

  const [firstAgents, setFirstAgents] = useState<OnboardingAgent[] | null>(null);
  const [agentTotal, setAgentTotal] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState<OnboardingAgent | null>(null);
  const [listForbidden, setListForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    onboarding.agents({ pageNumber: 1, pageSize: AGENT_PAGE_SIZE })
      .then((page) => {
        if (cancelled) return;
        setFirstAgents(page.items);
        setAgentTotal(page.totalCount);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) {
          setListForbidden(true);
        } else {
          setError(e instanceof Error ? e.message : "Failed to load agents.");
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (selected) return;
    const mine = user?.userNumber != null ? String(user.userNumber) : "";
    if (listForbidden) {
      if (mine) setSelected(mine);
      return;
    }
    if (mine) {
      setSelected(mine);
      return;
    }
    if (!firstAgents) return;
    setSelected(firstAgents.length > 0 ? String(firstAgents[0].userNumber) : "");
  }, [firstAgents, user, listForbidden, selected]);

  const [applyPath, setApplyPath] = useState<string>("");
  const [linkError, setLinkError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const n = Number(selected);
    if (!selected || Number.isNaN(n)) {
      setApplyPath("");
      setLinkError(null);
      return;
    }
    onboarding.applyLink(n)
      .then((r) => { if (!cancelled) { setApplyPath(r.path); setLinkError(null); } })
      .catch((e) => {
        if (cancelled) return;
        setApplyPath("");
        setLinkError(e instanceof Error ? e.message : "Couldn't load the onboarding link.");
      });
    return () => { cancelled = true; };
  }, [selected]);

  const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
  const [campaignId, setCampaignId] = useState<string>("");
  useEffect(() => {
    if (!FEATURES.applicationCampaigns) return;
    let cancelled = false;
    applicationCampaigns.listAll()
      .then((cs) => { if (!cancelled) setCampaigns(cs.filter((c) => c.status === "Active")); })
      .catch(() => {  });
    return () => { cancelled = true; };
  }, []);

  const link = useMemo(
    () => (applyPath
      ? url(`${applyPath}${campaignId ? `?c=${encodeURIComponent(campaignId)}` : ""}`)
      : ""),
    [applyPath, campaignId],
  );

  useEffect(() => {
    if (!selected) {
      setSelectedAgent(null);
      return;
    }
    if (selectedAgent && String(selectedAgent.userNumber) === selected) return;
    let cancelled = false;
    findAgentByNumber(selected)
      .then((a) => { if (!cancelled) setSelectedAgent(a); })
      .catch(() => { if (!cancelled) setSelectedAgent(null); });
    return () => { cancelled = true; };
  }, [selected, selectedAgent]);

  const agentName = useMemo(() => {
    if (selectedAgent && String(selectedAgent.userNumber) === selected) return selectedAgent.name;
    if (listForbidden) return user?.name ?? null;
    return null;
  }, [selectedAgent, selected, listForbidden, user]);

  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't reach the clipboard — select the link text and copy it manually.");
    }
  }

  const loading = firstAgents === null && !listForbidden && !error;
  const hasAgentList = firstAgents !== null && agentTotal > 0;
  const canExportLinks = user?.role === "ADMIN";
  const selfOnly = listForbidden && user?.userNumber != null;
  const noNumber = listForbidden && user?.userNumber == null;
  const showCard = hasAgentList || selfOnly;

  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">Onboarding Form</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Create and share onboarding links with prospects. Submitted forms will appear below for review.
        </p>
      </header>

      {error && <Notice tone="error" className="mb-4">{error}</Notice>}
      {loading && <Loading>Loading…</Loading>}

      {firstAgents !== null && agentTotal === 0 && (
        <Notice tone="info">
          No active agents with an agent number yet. Assign an agent number
          under Manage → Users, then come back here.
        </Notice>
      )}
      {noNumber && (
        <Notice tone="info">
          Your account doesn't have an agent number assigned, so there's no
          onboarding link to share yet. Ask an administrator to assign one
          under Manage → Users.
        </Notice>
      )}

      {showCard && (
        <div className="space-y-6">
          {Number(selected) > 0 && <PricedLinksCard agentNumber={Number(selected)} />}

          <Card>
            <CardHeader className="border-b py-3 flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1">
                <CardTitle className="text-base font-semibold text-slate-900">Onboarding Link Setup</CardTitle>
                <CardDescription>
                  Choose the user who owns applications from this link.
                </CardDescription>
              </div>
              {hasAgentList && canExportLinks && <ExportLinksMenu />}
            </CardHeader>
            <CardContent className="pt-6 space-y-5">
              {FEATURES.applicationCampaigns && (
                <Field label="Fees Campaign" optional>
                  {campaigns.length === 0 ? (
                    <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                      No active campaigns. Create one under Applications → Application Campaign.
                    </div>
                  ) : (
                    <Select
                      value={campaignId || "none"}
                      onValueChange={(v) => { setCampaignId(v === "none" ? "" : v); setCopied(false); }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No campaign</SelectItem>
                        {campaigns.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              )}

              <div className="grid gap-5 lg:grid-cols-2 lg:gap-6">
                <div className="space-y-3">
                  {hasAgentList ? (
                    <Field label="Share link for user" hint="Permission 30.87" htmlFor="shareForUser">
                      <AgentNumberSelect
                        id="shareForUser"
                        value={selected}
                        onValueChange={(v) => { setSelected(v); setCopied(false); }}
                      />
                    </Field>
                  ) : (
                    <div className="space-y-1.5">
                      <Label>Your onboarding link</Label>
                      <div className="text-sm text-slate-800">
                        {agentName} <span className="text-muted-foreground">#{selected}</span>
                      </div>
                    </div>
                  )}

                  {agentName && (
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Info className="h-3.5 w-3.5 shrink-0" />
                      <span>
                        Applications submitted through this link are attributed to{" "}
                        <span className="font-medium text-slate-700">{agentName} (#{selected})</span>.
                      </span>
                    </p>
                  )}
                </div>

                <Field label="Onboarding Link">
                  <div className="space-y-2">
                    <Input
                      type="text"
                      readOnly
                      value={link || (linkError ? "You don't have permission to display this sections" : "")}
                      onFocus={(e) => e.currentTarget.select()}
                      className="bg-muted font-mono text-slate-700"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      {Number(selected) > 0 && (
                        <RegenerateLinkButton
                          agentNumber={Number(selected)}
                          onDone={(_, path) => { setApplyPath(path); setCopied(false); }}
                        />
                      )}
                      <Button type="button" onClick={copy} disabled={!link} className="whitespace-nowrap">
                        <Copy />
                        {copied ? "Copied ✓" : "Copy link"}
                      </Button>
                      {link ? (
                        <Button asChild variant="outline" className="whitespace-nowrap">
                          <a href={link} target="_blank" rel="noreferrer">
                            <ExternalLink />
                            Open link
                          </a>
                        </Button>
                      ) : (
                        <Button type="button" variant="outline" disabled className="whitespace-nowrap">
                          <ExternalLink />
                          Open link
                        </Button>
                      )}
                    </div>
                  </div>
                </Field>
              </div>
            </CardContent>
          </Card>

          {link && (
            <ShareLinkCard
              link={link}
              formName="onboarding form"
              qrFilename={`onboarding-link-${selected || "qr"}.png`}
              onSend={async (to, toName) => {
                await onboarding.sendLink(Number(selected), to, toName, "APPLY");
                return to;
              }}
            />
          )}

          <NextStepsCard steps={NEXT_STEPS} />

          {FEATURES.onboardingRoadmapNotice && (
            <Notice tone="info">
              Still coming: SMS sending (needs the Twilio service) and
              fee-campaign attribution.
            </Notice>
          )}

          <SubmittedFormsList
            source="PUBLIC"
            hasAgents={hasAgentList}
            heading="Submitted Onboarding Forms"
            description="Track submissions from every onboarding link."
          />
        </div>
      )}
    </>
  );
}

function Field({
  label,
  optional,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  optional?: boolean;
  htmlFor?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>
        {label}
        {optional && <span className="text-muted-foreground font-normal ml-1">(optional)</span>}
        {hint && <span className="text-muted-foreground font-normal ml-1">({hint})</span>}
      </Label>
      {children}
    </div>
  );
}
